import React, { useState, useEffect, useMemo } from 'react';
import { ChartBarIcon, PencilIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Card from '../Card';
import Table from '../Table';
import Modal from '../Modal';
import Pagination from '../Pagination';
import SearchFilter from '../SearchFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const AchievementsSection = ({ api, users = [], organizations = [], onRefresh }) => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 5;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [usersWithAchievement, setUsersWithAchievement] = useState([]);
  const [availableUsersForAssign, setAvailableUsersForAssign] = useState([]);
  const [iconPreview, setIconPreview] = useState(null);
  
  const defaultForm = { title: '', description: '', icon: '', points: 0, is_repeatable: false, requirements: { type: 'none', value: '' } };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.fetchAchievements();
      setAchievements(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() =>
    achievements.filter(a => {
      const matchesSearch = a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesOrg = !orgFilter || 
        (orgFilter === 'global' && !a.organization_id) ||
        (orgFilter !== 'global' && a.organization_id === parseInt(orgFilter));
      return matchesSearch && matchesOrg;
    }), [achievements, searchQuery, orgFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const getReqText = (req) => {
    if (typeof req === 'string') try { req = JSON.parse(req); } catch { req = {}; }
    if (!req || req.type === 'none') return 'Нет требования';
    if (req.type === 'total_dialogs') return `Завершить ${req.value} диалогов`;
    if (req.type === 'time') return `Потратить ${req.value} секунд`;
    return 'Нет требования';
  };

  const getIconUrl = (icon) => {
    if (!icon) return null;
    if (icon.startsWith('http') || icon.startsWith('/api/')) return icon;
    return `${BACKEND_URL}/api/achievement_icon/${icon}`;
  };

  const handleIconUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = ev => setIconPreview(ev.target.result);
    reader.readAsDataURL(file);
    
    try {
      const url = await api.uploadAchievementIcon(file);
      setIconPreview(null);
      if (isEdit) {
        setCurrentAchievement(p => ({ ...p, icon: url }));
      } else {
        setForm(p => ({ ...p, icon: url }));
      }
      toast.success('Иконка загружена');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAdd = () => {
    setForm(defaultForm);
    setIconPreview(null);
    setShowAddModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createAchievement({
        name: form.title,
        description: form.description,
        icon: form.icon,
        points: parseInt(form.points, 10),
        is_repeatable: form.is_repeatable,
        requirements: form.requirements.type !== 'none' ? form.requirements : {}
      });
      toast.success('Достижение создано');
      setShowAddModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (achievement) => {
    let req = achievement.requirements;
    if (typeof req === 'string') try { req = JSON.parse(req); } catch { req = {}; }
    if (!req || !req.type) req = { type: 'none', value: '' };
    
    setCurrentAchievement({
      id: achievement.id,
      title: achievement.title,
      description: achievement.description,
      icon: achievement.icon || '',
      points: achievement.points || 0,
      is_repeatable: achievement.is_repeatable || false,
      requirements: { type: req.type || 'none', value: String(req.value || '') }
    });
    setIconPreview(null);
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateAchievement(currentAchievement.id, {
        name: currentAchievement.title,
        description: currentAchievement.description,
        icon: currentAchievement.icon,
        points: parseInt(currentAchievement.points, 10),
        is_repeatable: currentAchievement.is_repeatable,
        requirements: currentAchievement.requirements.type !== 'none' ? currentAchievement.requirements : {}
      });
      toast.success('Достижение обновлено');
      setShowEditModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (achievementId) => {
    if (!window.confirm('Удалить достижение?')) return;
    try {
      await api.deleteAchievement(achievementId);
      toast.success('Достижение удалено');
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssign = async (achievement) => {
    setCurrentAchievement(achievement);
    setShowAssignModal(true);
    
    try {
      const usersHaving = [];
      const usersNotHaving = [];
      
      for (const user of users) {
        const userAchievements = await api.fetchUserAchievements(user.id);
        if (userAchievements.some(ua => ua.id === achievement.id)) {
          usersHaving.push(user);
        } else {
          usersNotHaving.push(user);
        }
      }
      
      setUsersWithAchievement(usersHaving);
      setAvailableUsersForAssign(usersNotHaving);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssignToUser = async (userId) => {
    try {
      await api.assignAchievementToUser(userId, currentAchievement.id);
      toast.success('Достижение назначено');
      const user = availableUsersForAssign.find(u => u.id === userId);
      setUsersWithAchievement(prev => [...prev, user]);
      setAvailableUsersForAssign(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUnassignFromUser = async (userId) => {
    if (!window.confirm('Отменить достижение у пользователя?')) return;
    try {
      await api.unassignAchievementFromUser(userId, currentAchievement.id);
      toast.success('Достижение отменено');
      const user = usersWithAchievement.find(u => u.id === userId);
      setAvailableUsersForAssign(prev => [...prev, user]);
      setUsersWithAchievement(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    { header: 'Название', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.title}</span> },
    { header: 'Описание', render: (row) => <span className="text-gray-700 dark:text-gray-300">{row.description}</span> },
    { header: 'Организация', className: 'hidden sm:table-cell', render: (row) => <span className="text-gray-700 dark:text-gray-300">{row.organization_name || 'Общее'}</span> },
    { header: 'Требование', className: 'hidden xs:table-cell', render: (row) => <span className="text-gray-700 dark:text-gray-300">{getReqText(row.requirements)}</span> },
    { header: 'Действия', render: (row) => (
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => handleEdit(row)} className="bg-blue-600 text-white rounded-lg px-3 py-1 hover:bg-blue-700 flex items-center text-xs"><PencilIcon className="w-4 h-4 mr-1" />Ред.</button>
        <button onClick={() => handleDelete(row.id)} className="bg-red-600 text-white rounded-lg px-3 py-1 hover:bg-red-700 flex items-center text-xs"><TrashIcon className="w-4 h-4 mr-1" />Удал.</button>
        <button onClick={() => handleAssign(row)} className="bg-gray-500 text-white rounded-lg px-3 py-1 hover:bg-gray-600 flex items-center text-xs"><UserGroupIcon className="w-4 h-4 mr-1" />Назн.</button>
      </div>
    )}
  ];

  // Рендер формы inline (не как компонент, чтобы не терять фокус)
  const renderForm = (data, setData, onSubmit, submitText, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Название *</label>
        <input type="text" value={data.title} onChange={e => setData(p => ({ ...p, title: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" required />
      </div>
      <div>
        <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Описание *</label>
        <textarea value={data.description} onChange={e => setData(p => ({ ...p, description: e.target.value }))} className="w-full px-4 py-3 rounded-xl border dark:bg-gray-800 dark:text-gray-100 h-32 resize-none" required />
      </div>
      
      <div>
        <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">Иконка</label>
        <div className="flex gap-2 items-center">
          {iconPreview ? (
            <img src={iconPreview} alt="preview" className="w-12 h-12 rounded" />
          ) : data.icon && (
            <img src={getIconUrl(data.icon)} alt="icon" className="w-12 h-12 rounded" />
          )}
          <input type="file" accept=".png,.jpg,.jpeg,.gif,.svg" onChange={e => handleIconUpload(e, isEdit)} className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
        </div>
      </div>
      
      <div>
        <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Баллы</label>
        <input type="number" value={data.points} onChange={e => setData(p => ({ ...p, points: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" />
      </div>
      
      <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
        <input type="checkbox" checked={data.is_repeatable} onChange={e => setData(p => ({ ...p, is_repeatable: e.target.checked }))} className="accent-blue-600" />
        Повторяемое
      </label>
      
      <div>
        <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Тип требования</label>
        <select value={data.requirements.type} onChange={e => setData(p => ({ ...p, requirements: { ...p.requirements, type: e.target.value } }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100 mb-2">
          <option value="none">Нет</option>
          <option value="total_dialogs">Завершить диалогов</option>
          <option value="time">Время (секунд)</option>
        </select>
        {data.requirements.type !== 'none' && (
          <div>
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Значение</label>
            <input type="number" value={data.requirements.value} onChange={e => setData(p => ({ ...p, requirements: { ...p.requirements, value: e.target.value } }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" />
          </div>
        )}
      </div>
      
      <div className="flex gap-3">
        <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="flex-1 bg-gray-300 dark:bg-gray-600 text-black dark:text-gray-100 py-2 rounded-xl">Отмена</button>
        <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-xl hover:bg-green-700">{submitText}</button>
      </div>
    </form>
  );

  return (
    <div data-section="achievements">
      <Card title="Достижения" icon={ChartBarIcon} headerActions={
        <button onClick={handleAdd} className="bg-green-500 text-white rounded-lg px-3 py-1 hover:bg-green-700 text-xs sm:text-base">Добавить</button>
      }>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <SearchFilter value={searchQuery} onChange={setSearchQuery} placeholder="Поиск по названию или описанию" />
          <select value={orgFilter} onChange={e => { setOrgFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100 text-sm">
            <option value="">Все</option>
            <option value="global">Общие</option>
            {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <Table columns={columns} data={paginated} loading={loading} error={error} emptyMessage="Нет достижений" />
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Card>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Добавить достижение" size="md">
        {renderForm(form, setForm, handleCreate, 'Добавить')}
      </Modal>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Редактировать: ${currentAchievement?.title}`} size="md">
        {currentAchievement && renderForm(currentAchievement, setCurrentAchievement, handleUpdate, 'Сохранить', true)}
      </Modal>

      <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)} title={`Назначить: ${currentAchievement?.title}`} size="lg">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Уже имеют:</h3>
            <ul className="border rounded p-2 max-h-60 overflow-y-auto">
              {usersWithAchievement.length === 0 ? (
                <li className="text-gray-600 text-center py-2">Никто не имеет</li>
              ) : usersWithAchievement.map(u => (
                <li key={u.id} className="flex justify-between items-center py-1">
                  <span>{u.username} ({u.email})</span>
                  <button onClick={() => handleUnassignFromUser(u.id)} className="bg-red-500 text-white rounded px-3 py-1 text-sm hover:bg-red-700">Отменить</button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Доступные:</h3>
            <ul className="border rounded p-2 max-h-60 overflow-y-auto">
              {availableUsersForAssign.length === 0 ? (
                <li className="text-gray-600 text-center py-2">Все имеют</li>
              ) : availableUsersForAssign.map(u => (
                <li key={u.id} className="flex justify-between items-center py-1">
                  <span>{u.username} ({u.email})</span>
                  <button onClick={() => handleAssignToUser(u.id)} className="bg-green-500 text-white rounded px-3 py-1 text-sm hover:bg-green-700">Назначить</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AchievementsSection;
